import {z} from 'zod';
import {amountToMinor} from './documentPricing';
const money2=/^(?:0|[1-9]\d{0,11})(?:\.\d{1,2})?$/;
export const costingElementSchema=z.enum(['Material','Labor','Overhead','Landed']);
export type CostingElement=z.infer<typeof costingElementSchema>;
export const costingMapSchema=z.object({element:costingElementSchema,accountCode:z.string().trim().min(1).max(32)});
export type CostingMap=z.infer<typeof costingMapSchema>;
export interface CostingPolicyDefinition {
 laborRate:string;overheadPercent:string;landedPercent:string;maps:CostingMap[];
}
export const costingPolicyDraftSchema=z.object({
 requestId:z.string().uuid(),functionalCurrency:z.string().trim().regex(/^[A-Za-z]{3}$/).transform(v=>v.toUpperCase()),
 basis:z.string().trim().min(5).max(1000),laborRate:z.string().trim().regex(money2),
 overheadPercent:z.string().trim().regex(/^(?:0|[1-9]\d{0,2})(?:\.\d{1,2})?$/),
 landedPercent:z.string().trim().regex(/^(?:0|[1-9]\d{0,2})(?:\.\d{1,2})?$/),
 maps:z.array(costingMapSchema).min(1).max(16),
});
export const costingPolicyActionSchema=z.object({revision:z.number().int().positive(),action:z.enum(['activate','retire']),reason:z.string().trim().min(5).max(1000)});
export const costRollupDraftSchema=z.object({
 requestId:z.string().uuid(),sourceKind:z.enum(['ManufacturingOrder','Subcontract']),sourceId:z.string().min(1).max(150),
 notes:z.string().trim().max(2000).default(''),
});
export const costRollupActionSchema=z.object({revision:z.number().int().positive(),action:z.enum(['post','reverse']),reason:z.string().trim().min(5).max(1000)});
export const glBudgetSchema=z.object({
 requestId:z.string().uuid(),period:z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),accountCode:z.string().trim().min(1).max(32),
 amount:z.string().trim().regex(money2),basis:z.string().trim().min(5).max(1000),
});
export type CostingPolicyInput=z.input<typeof costingPolicyDraftSchema>;
export type CostRollupInput=z.input<typeof costRollupDraftSchema>;
export type GlBudgetInput=z.infer<typeof glBudgetSchema>;
export interface CostRollupSnapshot {
 sourceKind:'ManufacturingOrder'|'Subcontract';sourceId:string;sourceLabel:string;currency:string;
 materialMinor:number;laborMinor:number;overheadMinor:number;landedMinor:number;totalMinor:number;
 maps:CostingMap[];notes:string;
 policyVersion?:string;costSources?:{sourceId:string;amountMinor:number;currency:string;note:string|null}[];
}
export function requiredCostElements():CostingElement[]{return ['Material','Labor','Overhead','Landed'];}
export function resolveCostMap(maps:CostingMap[],element:CostingElement):string{
 const hit=maps.find(m=>m.element===element);if(!hit)throw Error('cost_unmapped');return hit.accountCode;
}
export function applyOverhead(materialMinor:number,laborMinor:number,percent:string):number{
 const [w,f='']=percent.trim().split('.');const bps=Number(w)*100+Number(f.padEnd(2,'0').slice(0,2));
 return Math.trunc((materialMinor+laborMinor)*bps/10000);
}
export function parseCostMoney(value:string):number{return amountToMinor(value,2);}
export interface CostingPolicyDto {
 id:string;version:string;status:'Draft'|'Active'|'Obsolete';revision:number;functionalCurrency:string;basis:string;definition:CostingPolicyDefinition;
}
export interface CostRollupDto {
 id:string;documentNo:string;sourceKind:'ManufacturingOrder'|'Subcontract';sourceId:string;status:'Draft'|'Posted'|'Reversed';
 revision:number;snapshot:CostRollupSnapshot;total:string;createdAt:number;
 events:{action:string;reason:string;createdAt:number;createdBy:string|null}[];
}
export interface GlBudgetDto {id:string;period:string;accountCode:string;amount:string;basis:string;revision:number;}
